<template>
    <!-- Centred: alone in its row at three quarters width, without a card. Otherwise a normal card beside the price table. -->
    <div
        id="product-faqs"
        :data-shortcut-title="$t('general.question_and_answers.title')"
        class="w-full"
        :class="centered ? 'md:mx-auto md:w-3/4' : 'lg:sticky lg:top-4 lg:h-fit'"
    >
        <div class="bg-white" :class="{ 'rounded-lg md:border md:border-border md:p-6': !centered }">
            <MobileCollapsibleSection>
                <!-- Lighter only as an accordion row under the group title; standalone it is a normal section header. -->
                <template #title="{ isCollapsible }">
                    <div class="text-left" :class="{ 'md:text-center': centered }">
                        <h2
                            class="md:text-2xl md:font-black onlywow:md:text-4xl"
                            :class="isCollapsible ? 'text-xl font-extrabold' : 'text-2xl font-extrabold'"
                            v-text="$t('general.question_and_answers.title')"
                        />
                        <p v-if="centered" class="mt-1 hidden text-sm text-gray-500 md:block" v-text="$t('general.question_and_answers.subtitle')" />
                    </div>
                </template>

                <template #default="{ isCollapsible }">
                    <span
                        v-if="faqs.length === 0"
                        class="text-sm text-gray-400 italic"
                        v-text="$t('general.question_and_answers.no_data.primary_text')"
                    />

                    <template v-for="faqGroup in faqs" :key="`faq_group_${faqGroup.category_title}`">
                        <!-- Centred on desktop, a lone category needs no heading; it only helps to tell several apart. -->
                        <div
                            v-if="faqGroup.faqs.length > 0"
                            class="mt-4 border-b border-b-gray-200 pb-2"
                            :class="{ 'first:mt-0.5': !isCollapsible, 'md:mt-6 md:border-b-0': centered, 'md:hidden': centered && faqs.length === 1 }"
                        >
                            <span v-text="faqGroup.category_title" class="text-lg font-bold text-primary" />
                        </div>

                        <!-- Centred on desktop: one bordered list with a line between the questions. -->
                        <div
                            v-if="faqGroup.faqs.length > 0"
                            :class="{ 'md:mt-6 md:overflow-hidden md:rounded-lg md:border md:border-gray-200': centered }"
                        >
                            <Accordion
                                :items="mapFaqsToAccordionItems(faqGroup.faqs)"
                                :chevron="centered ? 'down' : 'right'"
                                :item-padding="centered ? 'pt-3 pb-1 onlywow:py-4 md:px-4 md:py-3 onlywow:md:py-3' : 'pt-3 pb-1 onlywow:py-4'"
                                :item-class="centered ? 'pb-2 onlywow:pb-0 md:pb-0' : 'pb-2 onlywow:pb-0'"
                            >
                                <template #label="{ item }">
                                    <span
                                        class="text-left"
                                        :class="[
                                            isCollapsible ? 'font-normal onlywow:font-medium' : 'font-medium onlywow:font-semibold',
                                            { 'md:font-semibold': centered },
                                        ]"
                                        v-text="item.label"
                                    />
                                </template>

                                <template #content="{ item }">
                                    <span
                                        class="prose block pb-1 text-sm onlywow:pb-0"
                                        :class="{ 'md:px-4 md:pb-3': centered }"
                                        v-text="item.content"
                                    />
                                </template>
                            </Accordion>
                        </div>
                    </template>
                </template>
            </MobileCollapsibleSection>
        </div>
    </div>
</template>

<script setup lang="ts">
import Accordion from '@/components/accordions/Accordion.vue';
import MobileCollapsibleSection from '@/components/accordions/MobileCollapsibleSection.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import type { AccordionItemConfiguration } from '@/types/components/accordions/AccordionTypes';
import type { FaqDataObject } from '@/types/generated';
import { computed } from 'vue';

const { centered = false } = defineProps<{
    // Alone in its row (centred, no card) instead of a card beside another section.
    centered?: boolean;
}>();

const productPageStore = useProductPageStore();
const faqs = computed(() => productPageStore.configurableProduct.faqs);

const mapFaqsToAccordionItems = (groupFaqs: FaqDataObject[]): AccordionItemConfiguration[] =>
    groupFaqs.map((faq: FaqDataObject) => ({
        label: faq.question,
        content: faq.answer,
    }));
</script>
